/* eslint-disable react/jsx-props-no-spreading */
import { useCallback, useEffect, useMemo, useState } from 'react'
import { Button, Col, Divider, Form, Row } from 'antd'
import { Formik, useFormikContext } from 'formik'
import { useLocation, useNavigate } from 'react-router-dom'
import PropTypes from 'prop-types'
import * as Yup from 'yup'
import dayjs from 'dayjs'
import queryString from 'query-string'
import { useIsMobile } from '../../hooks'
import { formElementPropsGenerator } from '../../lib/forms'
import { DatePicker, Input, SubmitButton } from '../Forms'

import api from '../../api'
import { toPath } from '../../routes'
import DeviceModelSelect from '../Forms/DeviceModelSelect'
import PracticeSelect from '../Forms/PracticeSelect'
import AsyncSelect from '../Forms/AsyncSelect'

const { Item } = Form

const TRANSMISSION_DEFAULTS = {
  patientId: undefined,
  practiceId: undefined,
  firstName: '',
  lastName: '',
  dateOfBirth: undefined,
  deviceModelId: '',
  deviceSerialNumber: '',
}

const TRANSMISSION_LABELS = {
  patientId: 'Patient',
  firstName: 'First Name',
  lastName: 'Last Name',
  dateOfBirth: 'Date of Birth',
  practiceId: 'Practice',
  selectedPracticeId: 'Practice',
  deviceModelId: 'Model #',
  deviceSerialNumber: 'Serial Number',
  cptCode: 'CPT Code',
  icd10Code: 'ICD-10 Code',
  authorized: 'Authorized',
  practiceMrn: 'Practice MRN',
}

const propsFor = formElementPropsGenerator({
  formName: 'transmission-form',
  labels: TRANSMISSION_LABELS,
  placeholders: {
    deviceModelId: 'Select a new device model',
  },
  practice: { key: undefined, value: undefined },
})

// Keyed on `md`: below 768 the app is in its mobile layout and this form goes
// vertical, rather than holding a label column beside every input.
const labelCol = {
  md: { span: 6, offset: 0 },
}

const wrapperCol = {
  md: { span: 15 },
}

const actionsWrapperCol = {
  md: { span: 4, offset: 10 },
}

const patientCreationSchema = () => {
  const shape = {
    deviceModelId: Yup.string().required(),
    deviceSerialNumber: Yup.string().required(),
  }

  return Yup.object().shape(shape)
}

const buildValidationSchema = () => {
  const shape = {
    firstName: Yup.string().required(),
    lastName: Yup.string().required(),
    dateOfBirth: Yup.date().max(new Date()).required(),
    deviceModelId: Yup.string().required(),
    deviceSerialNumber: Yup.string().required(),
    useCurrentDevice: Yup.boolean().required(),
  }

  return Yup.object().shape(shape)
}

function PatientSelector({ showInactive = false, practiceId = undefined, ...rest }) {
  const fetchOptions = useCallback(
    q =>
      api.patients.getPatients({ q, showInactive, practiceId }).then(res =>
        res.body.map(patient => {
          const formattedBirthDate = dayjs(patient.birthDate).format(
            'YYYY-MM-DD',
          )
          const fullName = `${patient.firstName} ${patient.lastName}`
          let title = `${fullName} - ${formattedBirthDate}`
          if (!patient.active) {
            title = `${title} (Inactive)`
          }

          return { id: patient.id, name: title }
        }),
      ),
    [showInactive, practiceId],
  )

  return (
    <AsyncSelect
      asyncSearch
      fetchOptions={fetchOptions}
      initialFetch={false}
      showSearch
      optionFilterProp="children"
      placeholder="Start typing to find a patient"
      {...rest}
    />
  )
}

PatientSelector.propTypes = {
  showInactive: PropTypes.bool,
  practiceId: PropTypes.string,
}

function CustomPatientSelector({
  paramsPatientId = undefined,
  updatePractice,
  deviceSerialNumber = undefined,
  patientCreationTogler,
  setMatchedDevice,
  practice = null,
}) {
  const { values, setValues, setFieldValue } = useFormikContext()

  const { patientId } = values
  const id = paramsPatientId || patientId
  const [selectedPracticeId, setSelectedPracticeId] = useState()

  useMemo(() => {
    if (practice) {
      setValues({ ...TRANSMISSION_DEFAULTS, practice })
      setSelectedPracticeId(practice.value)
    }
  }, [practice, setValues, setSelectedPracticeId])

  useEffect(() => {
    if (id) {
      api.patients.get(id).then(response => {
        const useCurrentDevice =
          deviceSerialNumber === response.currentDevice?.serialNumber
        setValues({
          ...TRANSMISSION_DEFAULTS,
          patientId: response.id,
          firstName: response.firstName,
          lastName: response.lastName,
          dateOfBirth: dayjs(response.birthDate),
          practiceMrn: response.practiceMrn,
          deviceModelId: useCurrentDevice
            ? response.currentDevice.deviceModelId
            : undefined,
          deviceSerialNumber:
            deviceSerialNumber || response.currentDevice?.serialNumber,
          useCurrentDevice,
        })

        if (useCurrentDevice) setMatchedDevice(true)

        setFieldValue('practice', {
          value: response.practice.id,
          label: response.practice.name,
        })
        patientCreationTogler(false)
      })
    } else {
      setValues({ ...TRANSMISSION_DEFAULTS, deviceSerialNumber })
      patientCreationTogler(true)
    }
  }, [
    id,
    setValues,
    setFieldValue,
    deviceSerialNumber,
    updatePractice,
    patientCreationTogler,
    setMatchedDevice,
  ])
  if (paramsPatientId) {
    return null
  }

  return (
    <>
    <Item label="Practice">
      <PracticeSelect
        {...propsFor('practice')}
        labelAlign="right"
        labelInValue
        allowClear={false}
        value={values.practice}

        onChange={value => {
          setSelectedPracticeId(value.value)
          setFieldValue('practice', value)
        }}
      />
      </Item>
      <PatientSelector
        {...propsFor('patientId')}
        placeholder="Search by name, device model or serial number"
        allowClear
        showInactive
        practiceId={selectedPracticeId}
      />
    </>
  )
}

CustomPatientSelector.propTypes = {
  paramsPatientId: PropTypes.string,
  updatePractice: PropTypes.func.isRequired,
  deviceSerialNumber: PropTypes.string,
  patientCreationTogler: PropTypes.func.isRequired,
  setMatchedDevice: PropTypes.func.isRequired,
  practice: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    key: PropTypes.string,
  }),
}

function CreatePatientForm({ serialNumber, practiceId = undefined }) {
  const isMobile = useIsMobile()
  const validationSchema = useMemo(() => patientCreationSchema(), [])
  const initialValues = {
    deviceModelId: '',
    deviceSerialNumber: serialNumber,
    practiceId,
  }
  const navigate = useNavigate()

  const redirectToCreation = ({ deviceModelId, deviceSerialNumber }) => {
    navigate(
      toPath.patients
        .new()
        .concat(
          '?',
          queryString.stringify({ deviceModelId, deviceSerialNumber }),
        ),
    )
  }

  return (
    <Formik
      validationSchema={validationSchema}
      initialValues={initialValues}
      onSubmit={redirectToCreation}
    >
      {({ handleSubmit }) => (
        <Form
          layout={isMobile ? 'vertical' : 'horizontal'}
          onFinish={handleSubmit}
          labelCol={isMobile ? undefined : labelCol}
          wrapperCol={isMobile ? undefined : wrapperCol}
        >
          <Divider orientation="left">Device Information</Divider>
          <Row>
            <Col xs={24} md={19}>
              <DeviceModelSelect
                {...propsFor('deviceModelId')}
                labelCol={{ md: { span: 6 } }}
                labelAlign="right"
                wrapperCol={{ md: { span: 15 } }}
              />
            </Col>
          </Row>
          <Row>
            <Col xs={24} md={19}>
              <Input
                {...propsFor('deviceSerialNumber')}
                disabled
                labelCol={{ md: { span: 6 } }}
              />
            </Col>
          </Row>
          <Row>
            <Col xs={24} md={18}>
              <Form.Item wrapperCol={actionsWrapperCol}>
                <Button type="primary" htmlType="submit">
                  Create Patient
                </Button>
              </Form.Item>
            </Col>
          </Row>
        </Form>
      )}
    </Formik>
  )
}

CreatePatientForm.propTypes = {
  serialNumber: PropTypes.string.isRequired,
  practiceId: PropTypes.string,
}

export default function TransmissionForm({
  deviceSerialNumber = undefined,
  practice = null,
  onSubmit,
}) {
  const isMobile = useIsMobile()
  const validationSchema = useMemo(() => buildValidationSchema(), [])
  const location = useLocation()
  const [selectedPractice, setSelectedPractice] = useState()
  const [showPatientCreationForm, setShowPatientCreationForm] = useState(true)
  const [matchedDevice, setMatchedDevice] = useState(false)
  const searchParams = new URLSearchParams(location.search)
  const paramsPatientId = searchParams.get('patientId')

  useEffect(() => {
    if (practice) {
      setSelectedPractice({ value: practice.id, label: practice.name })
    }
  }, [practice])

  return (
    <>
      <Formik
        initialValues={TRANSMISSION_DEFAULTS}
        validationSchema={validationSchema}
        onSubmit={onSubmit}
      >
        {({ handleSubmit, isSubmitting, submitCount, values }) => (
          <Form
            layout={isMobile ? 'vertical' : 'horizontal'}
            onFinish={handleSubmit}
            labelCol={isMobile ? undefined : labelCol}
            wrapperCol={isMobile ? undefined : wrapperCol}
          >
            <CustomPatientSelector
              paramsPatientId={paramsPatientId}
              deviceSerialNumber={deviceSerialNumber}
              patientCreationTogler={setShowPatientCreationForm}
              setMatchedDevice={setMatchedDevice}
              practice={selectedPractice}
            />
            {values.patientId && (
              <>
                <Row>
                  <Col xs={24} md={19}>
                    <Input {...propsFor('firstName')} disabled />
                  </Col>
                </Row>
                <Row>
                  <Col xs={24} md={19}>
                    <Input {...propsFor('lastName')} disabled />
                  </Col>
                </Row>
                <Row>
                  <Col xs={24} md={19}>
                    <DatePicker
                      {...propsFor('dateOfBirth')}
                      disabled
                      showToday={false}
                    />
                  </Col>
                </Row>
                <Row>
                  <Col xs={24} md={19}>
                    <Input
                      {...propsFor('practiceMrn')}
                      disabled={!!values.patientId}
                    />
                  </Col>
                </Row>
                <Divider orientation="left">Device Information</Divider>
                <Row>
                  <Col xs={24} md={19}>
                    <DeviceModelSelect
                      {...propsFor('deviceModelId')}
                      labelCol={{ md: { span: 6 } }}
                      labelAlign="right"
                      wrapperCol={{ md: { span: 15 } }}
                      disabled={matchedDevice}
                    />
                  </Col>
                </Row>
                <Row>
                  <Col xs={24} md={19}>
                    <Input
                      {...propsFor('deviceSerialNumber')}
                      disabled
                      labelCol={{ md: { span: 6 } }}
                    />
                  </Col>
                </Row>
                <Row>
                  <Col xs={24} md={18}>
                    <SubmitButton
                      isSubmitting={isSubmitting}
                      title="Update Transmission"
                      isValid
                      submitCount={submitCount}
                      wrapperCol={actionsWrapperCol}
                    />
                  </Col>
                </Row>
              </>
            )}
          </Form>
        )}
      </Formik>
      {showPatientCreationForm && (
        <CreatePatientForm serialNumber={deviceSerialNumber} />
      )}
    </>
  )
}

TransmissionForm.propTypes = {
  deviceSerialNumber: PropTypes.string,
  practice: PropTypes.shape({ id: PropTypes.string, name: PropTypes.string }),
  onSubmit: PropTypes.func.isRequired,
}
